import { ModuleObject } from '../types';

import {
  DEFAULT_API_METHOD,
  DEFAULT_BASE_URL,
  DEFAULT_METHODS,
  DEFAULT_MIDDLEWARE,
  DEFAULT_TRANSPORT,
  DefaultRequestConfig,
} from './config';
import { NamespaceRoutes, RouteFor } from './routes';
import {
  ApiMethod,
  ApiTransport,
  Middlewares,
  PromiseApi,
  RequestConfig,
  RequestMiddleware,
  RequestParams,
} from './types';

const fixedMiddleware = (middleware: Middlewares): Middlewares => Object.freeze({
  request: Object.freeze([...middleware.request]),
  response: Object.freeze([...middleware.response]),
});

type SendProps<T extends ApiMethod, ConfigT extends RequestConfig> = {
  base?: string;
  namespace: string;
  routes?: NamespaceRoutes;
  method: string;
  payload: T['payload'];
  middleware?: Middlewares;
  config?: ConfigT;
  transport?: ApiTransport;
};

export default class Api<
  C extends RequestConfig = DefaultRequestConfig,
  ApiMethodT extends ApiMethod = DEFAULT_API_METHOD,
  ApiMethodsMap extends { [key: string]: ApiMethod } = { [key: string]: ApiMethod }
> {
  // The base is the service and version this client speaks to (/api/auth/v1). A function is read at each
  // request, for the apps whose own service is only known once the app has started.
  constructor(
    private base: string | (() => string) = DEFAULT_BASE_URL,
    middleware: Middlewares = DEFAULT_MIDDLEWARE,
    private transport: ApiTransport = DEFAULT_TRANSPORT
  ) {
    this.middleware = fixedMiddleware(middleware);
  }

  private middleware: Middlewares;

  public setMiddleware = (middleware: Middlewares) => {
    this.middleware = fixedMiddleware(middleware);
  };

  public makeApi = <T extends ApiMethodT>(route: RouteFor) => (
    payload: T['payload'],
    config?: C
  ) => this.send<T, C>({ namespace: '', routes: { routes: { '': route } }, method: '', payload, config });

  public namespace = <ModuleT extends ModuleObject, CustomMethodsT extends ApiMethodsMap>(
    namespace: string,
    routes?: NamespaceRoutes
  ) => {
    type Methods = DEFAULT_METHODS<ModuleT>;
    return <KeyT extends keyof Methods | keyof CustomMethodsT>(
      name: KeyT,
      payload: KeyT extends keyof Methods
        ? Methods[KeyT]['payload']
        : CustomMethodsT[KeyT]['payload'],
      config?: C
    ): PromiseApi<KeyT extends keyof Methods ? Methods[KeyT] : CustomMethodsT[KeyT]> =>
      this.send<KeyT extends keyof Methods ? Methods[KeyT] : CustomMethodsT[KeyT], C>({
        namespace,
        routes,
        method: name.toString(),
        payload,
        config,
      });
  };

  public send = <T extends ApiMethod, ConfigT extends RequestConfig>({
    base = typeof this.base === 'function' ? this.base() : this.base,
    namespace,
    routes,
    method,
    payload,
    middleware,
    config,
    transport = this.transport,
  }: SendProps<T, ConfigT>): PromiseApi<T> => {
    type Params = Omit<RequestParams, 'method'>;
    const result: Params = { payload, config };
    const requestMiddleware = this.middleware.request.concat(middleware?.request || []);
    const { payload: payloadToGo, config: configToGo } = requestMiddleware.reduce(
      (res: Params, cb: RequestMiddleware) =>
        cb({ method, payload: res.payload, config: res.config }),
      result
    );

    const req = transport({
      base,
      namespace,
      routes,
      method,
      payload: payloadToGo,
      config: configToGo,
    });

    const responseMiddleware = this.middleware.response.concat(middleware?.response || []);
    return responseMiddleware.reduce(
      (res, cb) => cb(res, { payload: payloadToGo, config: configToGo }),
      req
    );
  };
}
